import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Markdown } from '~/components/chat/Markdown';
import { apiGetSharedWork, type SharedWork, type SharedWorkFile } from '~/lib/api/work-shares';

/** Where "Get Dunea Work" goes — the desktop download. */
const GET_WORK_URL = 'https://dunea.ai';

/** A file's type colour and label — the same as Dunea Work's file icons, so a shared file looks like it does in the app. */
const TYPE: Record<string, { color: string; label: string; kind: string }> = {
  pdf: { color: '#E5484D', label: 'PDF', kind: 'PDF' },
  doc: { color: '#2F6FEB', label: 'DOC', kind: 'Document' },
  docx: { color: '#2F6FEB', label: 'DOCX', kind: 'Document' },
  xls: { color: '#1FA463', label: 'XLS', kind: 'Spreadsheet' },
  xlsx: { color: '#1FA463', label: 'XLSX', kind: 'Spreadsheet' },
  csv: { color: '#12A594', label: 'CSV', kind: 'Spreadsheet' },
  ppt: { color: '#E8741D', label: 'PPT', kind: 'Presentation' },
  pptx: { color: '#E8741D', label: 'PPTX', kind: 'Presentation' },
  md: { color: '#8B8D98', label: 'MD', kind: 'Document' },
  txt: { color: '#8B8D98', label: 'TXT', kind: 'Text' },
  png: { color: '#8E4EC6', label: 'PNG', kind: 'Image' },
  jpg: { color: '#8E4EC6', label: 'JPG', kind: 'Image' },
  jpeg: { color: '#8E4EC6', label: 'JPG', kind: 'Image' },
  html: { color: '#E5733A', label: 'HTML', kind: 'Web page' },
};

function typeOf(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return TYPE[ext] ?? { color: '#6F6F78', label: (ext || 'file').slice(0, 4).toUpperCase(), kind: 'File' };
}

function sizeOf(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function FileIcon({ name }: { name: string }) {
  const t = typeOf(name);

  return (
    <svg viewBox="0 0 32 40" width={29} height={36} className="flex-shrink-0" aria-label={`${t.label} file`}>
      <path
        d="M5 1.5h16.5L30 10v26.5A2.5 2.5 0 0 1 27.5 39h-22A2.5 2.5 0 0 1 3 36.5v-32.5A2.5 2.5 0 0 1 5 1.5z"
        fill={t.color}
        fillOpacity="0.14"
        stroke={t.color}
        strokeOpacity="0.55"
      />
      <path d="M21.5 1.5V8a2 2 0 0 0 2 2H30" fill={t.color} fillOpacity="0.28" stroke={t.color} strokeOpacity="0.55" />
      <path d="M3 26h27v10.5A2.5 2.5 0 0 1 27.5 39h-22A2.5 2.5 0 0 1 3 36.5z" fill={t.color} />
      <text x="16.5" y="35.1" textAnchor="middle" fontSize={t.label.length > 3 ? 7 : 8.4} fontWeight="800" fill="#fff">
        {t.label}
      </text>
    </svg>
  );
}

function FileCard({ file }: { file: SharedWorkFile }) {
  return (
    <a
      href={file.url}
      download={file.name}
      className="group flex items-center gap-3 px-3.5 py-3 rounded-xl bg-surface border border-hairline hover:border-subtle hover:bg-raised transition-colors no-underline max-w-[520px]"
    >
      <FileIcon name={file.name} />
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] text-primary font-medium truncate">{file.name}</span>
        <span className="block text-[12px] text-muted mt-0.5">
          {typeOf(file.name).kind} · {sizeOf(file.size)}
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-[12.5px] text-secondary group-hover:text-primary flex-shrink-0">
        <span className="i-ph:download-simple text-[15px]" /> Download
      </span>
    </a>
  );
}

/**
 * A Dunea Work chat someone shared (app.dunea.ai/share/:id) — read-only, no
 * sign-in. It reads the way it did in the app: the question, a line on what
 * Dunea did, the answer, and the files it made to download.
 */
export default function SharedWorkPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery<SharedWork>({
    queryKey: ['shared-work', id],
    queryFn: () => apiGetSharedWork(id!),
    enabled: !!id,
    retry: false,
  });

  useEffect(() => {
    document.title = data ? `${data.title} · Dunea Work` : 'Dunea Work';
  }, [data]);

  // files a message names show under it; any it doesn't are listed at the end
  const { byName, loose } = useMemo(() => {
    const files = data?.files ?? [];
    const named = new Set(data?.messages.flatMap((m) => (m.role === 'assistant' ? (m.files ?? []) : [])) ?? []);

    return {
      byName: new Map(files.map((f) => [f.name, f])),
      loose: files.filter((f) => !named.has(f.name)),
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="ui-root min-h-dvh bg-page flex items-center justify-center">
        <p className="text-secondary" style={{ fontSize: 13 }}>
          Loading…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="ui-root min-h-dvh bg-page flex items-center justify-center px-4">
        <div className="text-center max-w-[380px]">
          <span className="i-ph:link-break text-[32px] text-muted block mx-auto mb-3" />
          <h2 className="ui-serif text-primary" style={{ fontSize: 24, margin: '0 0 6px' }}>
            This link isn’t available.
          </h2>
          <p className="text-secondary mb-5" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            The person who shared it may have turned it off.
          </p>
          <Link to="/" className="ui-btn-accent">
            Go to Dunea
          </Link>
        </div>
      </div>
    );
  }

  const when = new Date(data.updatedAt).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="ui-root min-h-dvh bg-page flex flex-col">
      {/* top bar: whose app this is, and the way to get it */}
      <header className="sticky top-0 z-10 flex items-center gap-2.5 px-5 h-14 bg-page/85 backdrop-blur-md border-b border-hairline">
        <img src="/dunea-mark.svg" alt="" className="w-5 h-5" />
        <span className="text-[14px] font-semibold text-primary tracking-[-0.01em]">Dunea Work</span>
        <span className="text-[12px] text-muted border border-hairline rounded-full px-2 py-0.5 ml-1">Shared chat</span>
        <span className="flex-1" />
        <a href={GET_WORK_URL} className="ui-btn-accent no-underline" style={{ padding: '7px 14px', fontSize: 13 }}>
          Get Dunea Work
        </a>
      </header>

      <main className="flex-1 w-full max-w-[780px] mx-auto px-5 pt-10 pb-16">
        <h1 className="ui-serif text-primary" style={{ fontSize: 34, lineHeight: 1.15, margin: 0 }}>
          {data.title}
        </h1>
        <p className="text-muted mt-2.5 mb-8" style={{ fontSize: 13 }}>
          {data.author ? `Shared by ${data.author} · ` : ''}
          {when}
        </p>

        <div className="flex flex-col gap-6">
          {data.messages.map((m, i) =>
            m.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] bg-elevated border border-subtle rounded-2xl px-4 py-3 text-[15px] leading-[1.65] text-primary whitespace-pre-wrap">
                  {m.text}
                </div>
              </div>
            ) : (
              <div key={i} className="flex flex-col gap-3">
                {m.worked && (
                  <div className="flex items-center gap-2 text-[13px] text-muted">
                    <img src="/dunea-mark.svg" alt="" className="w-3.5 h-3.5" />
                    {m.worked}
                  </div>
                )}
                {m.text && (
                  <div className="text-[15.5px] leading-[1.7] text-primary">
                    <Markdown>{m.text}</Markdown>
                  </div>
                )}
                {(m.files ?? [])
                  .map((n) => byName.get(n))
                  .filter((f): f is SharedWorkFile => Boolean(f))
                  .map((f) => (
                    <FileCard key={f.name} file={f} />
                  ))}
              </div>
            ),
          )}
        </div>

        {loose.length > 0 && (
          <section className="mt-10">
            <div className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-secondary mb-3">Files</div>
            <div className="flex flex-col gap-2">
              {loose.map((f) => (
                <FileCard key={f.name} file={f} />
              ))}
            </div>
          </section>
        )}

        {/* the invitation — what this was made with, and a way to try it */}
        <div className="mt-14 rounded-2xl border border-hairline bg-surface px-6 py-5 flex items-center gap-4 flex-wrap">
          <img src="/dunea-mark.svg" alt="" className="w-8 h-8" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-[15px] text-primary font-medium">Made with Dunea Work</div>
            <div className="text-[13px] text-muted mt-0.5">
              Research, spreadsheets, documents and decks — done for you on your Mac.
            </div>
          </div>
          <a href={GET_WORK_URL} className="ui-btn-accent no-underline" style={{ padding: '8px 16px', fontSize: 13.5 }}>
            Try it free
          </a>
        </div>
        <p className="text-center text-faint mt-6" style={{ fontSize: 12 }}>
          A read-only copy of a Dunea Work chat. Dunea Work is AI and can make mistakes — check important info.
        </p>
      </main>
    </div>
  );
}
